import { DescService } from "@bufbuild/protobuf";
import { createClient, Interceptor } from "@connectrpc/connect";
import { Code, ConnectError } from "@connectrpc/connect";
import { createConnectTransport } from "@connectrpc/connect-web";
import { useUserStore as userStore } from "@stores/userStore";
import { useCartStore as cartStore } from "@stores/cartStore";
import { useBookingStore as bookingStore } from "@stores/useBookingStore";
import { useToastStore as toastStore } from "@stores/toastStore";
import i18n from "@libs/i18n";
import * as Sentry from "@sentry/react";
import { ory } from "@services/authentication";

const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:8080";

let isLoggingOut = false;

function logoutUser() {
  if (isLoggingOut) return;
  isLoggingOut = true;

  userStore.getState().clearUser();
  cartStore.getState().clearCart();
  bookingStore.getState().resetBookingForm();

  ory()
    .createBrowserLogoutFlow()
    .then(({ data: flow }) =>
      ory().updateLogoutFlow({ token: flow.logout_token }),
    )
    .catch(() => {})
    .finally(() => {
      isLoggingOut = false;
    });
}

const errorInterceptor: Interceptor = (next) => async (req) => {
  try {
    return await next(req);
  } catch (e) {
    const err = ConnectError.from(e);
    if (
      err.code === Code.Unauthenticated &&
      userStore.getState().user !== null &&
      !isLoggingOut
    ) {
      toastStore.getState().addToast("info", i18n.t("sessionExpired"));
      logoutUser();
    }

    const requestID = err.metadata.get("X-Request-ID");
    if (requestID) {
      Object.assign(err, {
        rawMessage: `${err.rawMessage} (ref: ${requestID})`,
      });
    }
    throw e;
  }
};

const addLanguageHeaderInterceptor: Interceptor = (next) => async (req) => {
  const language = i18n.language;
  req.header.append("Accept-Language", language);
  return await next(req);
};

export const sentryTracingInterceptor: Interceptor = (next) => async (req) => {
  // Grab trace data from Sentry SDK
  const traceData = Sentry.getTraceData();

  // Add sentry headers if present
  if (traceData["sentry-trace"]) {
    req.header.set("sentry-trace", traceData["sentry-trace"]);
  }
  if (traceData["baggage"]) {
    req.header.set("baggage", traceData["baggage"]);
  }

  return next(req);
};

export function client<T extends DescService>(service: T) {
  // we need a custom fetch to allow sending credential cookies
  const customFetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    return fetch(input, {
      ...init,
      credentials: "include",
    });
  };
  const transport = createConnectTransport({
    baseUrl: apiUrl,
    fetch: customFetch,
    interceptors: [
      errorInterceptor,
      addLanguageHeaderInterceptor,
      sentryTracingInterceptor,
    ],
  });
  return createClient(service, transport);
}
